import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VideocamIcon from "@mui/icons-material/Videocam";
import DescriptionIcon from "@mui/icons-material/Description";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import { getTaskFileKindLabel, resolveTaskFileKind } from "../../utils/taskFormFileKind";
import {
  taskFormFloatingButtonSx,
  taskFormNestedBoxSx,
  taskFormSoftBoxSx,
} from "../../utils/taskFormStyles";

/**
 * Thumbnail tile for task / task-template form file attachments.
 */
export default function TaskFormFileTile({ file, onPreview, onRemove, canDelete }) {
  const kind = resolveTaskFileKind(file);
  const typeLabel = getTaskFileKindLabel(kind);
  const displayName = file.name || file.originalName || "File";

  const handlePreviewClick = () => {
    onPreview(file, kind);
  };

  const handleRemoveClick = (event) => {
    event.stopPropagation();
    onRemove(file.id);
  };

  const handlePreviewKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handlePreviewClick();
    }
  };

  const renderPreviewBody = () => {
    if (kind === "image") {
      return (
        <Box
          component="img"
          src={file.url}
          alt={displayName}
          sx={{ width: "100%", height: 52, objectFit: "cover", borderRadius: 1, display: "block" }}
        />
      );
    }

    const KindIcon =
      kind === "video" ? VideocamIcon : kind === "audio" ? AudiotrackIcon : DescriptionIcon;

    return (
      <Box
        sx={{
          ...taskFormSoftBoxSx,
          width: "100%",
          height: 52,
          borderRadius: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.25,
        }}
      >
        <KindIcon sx={{ fontSize: 22, opacity: 0.85 }} />
        <Typography variant="caption" sx={{ fontSize: "0.65rem", lineHeight: 1, opacity: 0.9 }}>
          {typeLabel}
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: 90,
        ...taskFormNestedBoxSx,
        p: 0.5,
        "& .file-delete-btn": {
          opacity: 0,
          pointerEvents: "none",
          transition: "opacity 0.2s ease",
        },
        "&:hover .file-delete-btn": {
          opacity: 1,
          pointerEvents: "auto",
        },
      }}
    >
      <IconButton
        size="small"
        className="file-delete-btn"
        onClick={handleRemoveClick}
        disabled={!canDelete}
        sx={{
          position: "absolute",
          top: -8,
          right: -8,
          ...taskFormFloatingButtonSx,
          p: 0.1,
          zIndex: 2,
        }}
      >
        <CloseIcon sx={{ fontSize: 13 }} />
      </IconButton>

      <Box
        onClick={handlePreviewClick}
        onKeyDown={handlePreviewKeyDown}
        role="button"
        tabIndex={0}
        aria-label={`Open ${typeLabel}: ${displayName}`}
        sx={{ cursor: "pointer" }}
      >
        {renderPreviewBody()}
      </Box>

      <Typography variant="caption" sx={{ mt: 0.5, display: "block" }} noWrap title={displayName}>
        {displayName}
      </Typography>
    </Box>
  );
}
